import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import clsx from "clsx";
import AddIcon from "@material-ui/icons/Add";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import ProjectIcon from "@material-ui/icons/LayersOutlined";
import ScheduleIcon from "@material-ui/icons/ScheduleOutlined";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useProject } from "../../context/Project/ProjectContext";
import { useConfirm } from "../../components/ConfirmDialog";
import { useMenuStyles } from "../../components/menuStyles";

// dias da semana (cron dow 0-6, 0=Dom)
const WD = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const pad2 = n => String(n).padStart(2, "0");

// parse de um cron "m h dom mon dow" (só formatos simples gerados pelo editor)
const parseCron = cron => {
  const p = String(cron || "").trim().split(/\s+/);
  if (p.length !== 5) return null;
  const [m, h, dom, mon, dow] = p;
  if (!/^\d+$/.test(m) || !/^\d+$/.test(h) || mon !== "*") return null;
  const time = `${pad2(h)}:${pad2(m)}`;
  if (dom === "*" && dow === "*") return { kind: "daily", time };
  if (dow !== "*" && dom === "*") return { kind: "weekly", time, dows: dow.split(",").map(Number).filter(n => !isNaN(n)) };
  if (dom !== "*" && dow === "*" && /^\d+$/.test(dom)) return { kind: "monthly", time, dom: Number(dom) };
  return null;
};

// resumo legível do agendamento, cobrindo os formatos: uma vez (runAt),
// diário / semanal / mensal (cron), múltiplos horários, ou cron cru (fallback).
const formatSchedule = cfg => {
  if (!cfg) return "—";
  if (cfg.runAt) {
    const d = new Date(cfg.runAt);
    if (!isNaN(d)) return `Uma vez · ${d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}`;
  }
  const crons = cfg.crons && cfg.crons.length ? cfg.crons : cfg.cron ? [cfg.cron] : [];
  if (!crons.length) return "—";
  const parsed = crons.map(parseCron);
  if (parsed.some(x => !x)) return crons.join(" · "); // fallback: cron cru
  const times = [...new Set(parsed.map(x => x.time))].sort().join(", ");
  const k = parsed[0].kind;
  if (k === "daily") return `Todos os dias às ${times}`;
  if (k === "weekly") {
    const dows = [...new Set(parsed.flatMap(x => x.dows))].sort((a, b) => a - b);
    return `${dows.map(d => WD[d % 7]).join(", ")} às ${times}`;
  }
  if (k === "monthly") return `Dia ${parsed[0].dom} de cada mês às ${times}`;
  return crons.join(" · ");
};

const useStyles = makeStyles(theme => ({
  page: { height: "100%", overflowY: "auto", background: theme.app.chatBg },
  wrap: { width: "100%", maxWidth: 1000, margin: "0 auto", padding: theme.spacing(3.5, 3, 6) },
  header: { display: "flex", alignItems: "flex-start", gap: 12, marginBottom: theme.spacing(2.5) },
  title: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 22, color: theme.app.ink },
  subtitle: { color: theme.app.textTertiary, fontSize: 13, marginTop: 4 },
  grow: { flexGrow: 1 },
  tbl: { background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 10, overflow: "hidden" },
  row: { display: "flex", alignItems: "center", gap: 12, padding: "0 16px", height: 52, borderBottom: `1px solid ${theme.app.lineSoft}`, cursor: "pointer", "&:hover": { background: theme.app.hover }, "&:hover $act": { visibility: "visible" }, "&:last-child": { borderBottom: "none" } },
  ic: { width: 30, height: 30, borderRadius: 8, background: theme.app.lineSoft, display: "flex", alignItems: "center", justifyContent: "center", color: theme.app.textSecondary, flexShrink: 0, "& svg": { fontSize: 17 } },
  name: { fontSize: 13.5, color: theme.app.ink, fontWeight: 500 },
  desc: { fontSize: 12, color: theme.app.textTertiary, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 520 },
  when: { fontSize: 12, color: theme.app.textSecondary, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 560 },
  pub: { fontFamily: theme.app.mono, fontSize: 11, padding: "2px 7px", borderRadius: 5 },
  pubOn: { background: "#E6F4EC", color: theme.app.ok },
  pubOff: { background: "#FBEFE0", color: theme.app.warn },
  act: { visibility: "hidden", color: theme.app.textTertiary },
  empty: { background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 10, padding: theme.spacing(6, 2), textAlign: "center", color: theme.app.textTertiary, fontSize: 13, lineHeight: 1.6 },
}));

const Schedules = () => {
  const classes = useStyles();
  const menu = useMenuStyles();
  const history = useHistory();
  const { confirm } = useConfirm();
  const { activeProjectId, projects } = useProject();
  const [items, setItems] = useState([]);
  const [rowMenu, setRowMenu] = useState(null); // { anchor, a }
  const [projMenu, setProjMenu] = useState(null); // { anchor, a } — mover p/ projeto

  const load = useCallback(async () => {
    if (!activeProjectId) return;
    try {
      const { data } = await api.get("/applications", { params: { projectId: activeProjectId } });
      setItems((data.applications || []).filter(a => a.triggerType === "scheduled"));
    } catch (err) {
      toastError(err);
    }
  }, [activeProjectId]);

  useEffect(() => {
    load();
  }, [load]);

  const create = () => {
    if (!activeProjectId) return;
    history.push(`/applications/new?projectId=${activeProjectId}&kind=scheduled`);
  };
  const open = a => history.push(`/applications/${a.code || a.id}`);

  const remove = async a => {
    setRowMenu(null);
    if (!(await confirm({ title: "Remover agendamento", message: `Remover "${a.name}"?`, confirmText: "Remover", danger: true }))) return;
    try {
      await api.delete(`/applications/${a.id}`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  // move o agendamento (Application "scheduled") para outro projeto. Some desta lista.
  const moveToProject = async (a, projectId) => {
    setProjMenu(null);
    try {
      await api.put(`/applications/${a.id}/project`, { projectId });
      const proj = projects.find(p => p.id === projectId);
      toast.success(`Agendamento movido para "${(proj && proj.name) || "o projeto"}".`);
      setItems(list => list.filter(x => x.id !== a.id));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        <div className={classes.header}>
          <div>
            <Typography className={classes.title}>Agendamentos</Typography>
            <Typography className={classes.subtitle}>
              Fluxos que disparam em horários definidos (gatilho "Agendado") — ex.: resumo diário,
              lembrete, cobrança recorrente.
            </Typography>
          </div>
          <div className={classes.grow} />
          <Button variant="contained" color="primary" disableElevation startIcon={<AddIcon />} onClick={create} disabled={!activeProjectId}>
            Novo
          </Button>
        </div>

        {items.length === 0 ? (
          <div className={classes.empty}>
            Nenhum agendamento ainda.<br />
            Crie o primeiro para disparar um fluxo em horários definidos.
          </div>
        ) : (
          <div className={classes.tbl}>
            {items.map(a => (
              <div key={a.id} className={classes.row} onClick={() => open(a)}>
                <div className={classes.ic}><ScheduleIcon /></div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className={classes.name}>{a.name}</div>
                  <div className={classes.when}>
                    {formatSchedule(a.triggerConfig)}
                    {a.triggerConfig && a.triggerConfig.timezone && a.triggerConfig.timezone !== "America/Sao_Paulo"
                      ? ` · ${a.triggerConfig.timezone.split("/").pop().replace(/_/g, " ")}`
                      : ""}
                  </div>
                </div>
                <span className={`${classes.pub} ${a.publishedVersion ? classes.pubOn : classes.pubOff}`}>
                  {a.publishedVersion ? `publicado v${a.publishedVersion}` : "rascunho"}
                </span>
                <span className={classes.act} onClick={e => e.stopPropagation()} style={{ display: "flex" }}>
                  <IconButton size="small" onClick={e => setRowMenu({ anchor: e.currentTarget, a })}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <Menu anchorEl={rowMenu ? rowMenu.anchor : null} open={!!rowMenu} onClose={() => setRowMenu(null)} PaperProps={{ className: menu.paper }} MenuListProps={{ disablePadding: true }}>
        <MenuItem className={menu.item} onClick={e => rowMenu && setProjMenu({ anchor: e.currentTarget, a: rowMenu.a })}>
          <ProjectIcon fontSize="small" />
          Mover para projeto…
        </MenuItem>
        <MenuItem className={clsx(menu.item, menu.danger)} onClick={() => rowMenu && remove(rowMenu.a)}>
          <DeleteIcon fontSize="small" />
          Remover agendamento
        </MenuItem>
      </Menu>

      {/* project picker — submenu de "Mover para projeto…" */}
      <Menu
        anchorEl={projMenu ? projMenu.anchor : null}
        open={!!projMenu}
        onClose={() => { setProjMenu(null); setRowMenu(null); }}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: menu.paper }}
        MenuListProps={{ disablePadding: true }}
      >
        {projects.length <= 1 && <MenuItem className={menu.item} disabled>Nenhum outro projeto</MenuItem>}
        {projects.map(p => {
          const isCurrent = p.id === activeProjectId;
          return (
            <MenuItem
              key={p.id}
              className={menu.item}
              disabled={isCurrent}
              onClick={() => { const a = projMenu && projMenu.a; setRowMenu(null); if (a) moveToProject(a, p.id); }}
            >
              {p.name}{isCurrent ? " (atual)" : ""}
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
};

export default Schedules;
